import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Divider, Stack, Typography } from '@mui/material';
import { DrawerContent } from 'components/common/drawer/DrawerContent';
import { FormSelectField } from 'components/common/form/Form';
import { VariantField } from 'modules/marketplace/VariantField';
import { FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { addProductSchema } from 'validation/form/marketplace';
import { addQuoteProduct, setDefaultSelectedQuote } from 'store/slices/marketplace';
import { useAsyncDispatch } from 'hooks/useAsyncDispatch';
import { EntityFileDetails } from 'components/shared/EntityFileDetails';

export const DetailsItem = (props) => {
  const { title, subtitle } = props;

  return (
    <Stack direction="column">
      <Typography
        component="h3"
        variant="caption"
        sx={{ fontWeight: 600 }}
      >
        {title}
      </Typography>
      <Typography
        component="h2"
        variant="subtitle2"
        sx={{
          fontWeight: 400,
          minWidth: '300px',
          width: '300px',
          maxWidth: '300px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {subtitle}
      </Typography>
    </Stack>
  );
};

DetailsItem.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
};

export const AddQuoteProduct = (props) => {
  const { close } = props;
  const dispatch = useDispatch();
  const dispatchAction = useAsyncDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const selectedProduct = useSelector((state) => state.marketplace.selectedProduct);
  const quotes = useSelector((state) => state.marketplace.quotes.value);
  const loading = useSelector((state) => state.marketplace.addQuoteProduct.loading);
  const defaultSelectedQuoteId = useSelector((state) => state.marketplace.addQuoteProduct.defaultSelectedQuote);
  const variants = useMemo(() => selectedProduct?.variants || [], [selectedProduct]);

  const methods = useForm({
    resolver: yupResolver(addProductSchema),
    defaultValues: {
      quote: '',
      variant: '',
    },
  });

  const handleSave = methods.handleSubmit((data) => {
    const { quote, variant } = data;

    dispatchAction(addQuoteProduct({ quoteId: quote, variantId: variant }), {
      onSuccess: () => {
        dispatch(setDefaultSelectedQuote(quote));
        enqueueSnackbar('Product has been added');
        close();
      },
    });
  });

  useEffect(() => {
    if (defaultSelectedQuoteId || quotes[0]?.id) {
      methods.reset({
        quote: defaultSelectedQuoteId || quotes[0]?.id,
        variant: '',
      });
    }
  }, [quotes, methods, defaultSelectedQuoteId]);

  return (
    <DrawerContent>
      <DrawerContent.Header title="Product" />
      <DrawerContent.Wrapper>
        <DrawerContent.Content>
          <FormProvider {...methods}>
            {selectedProduct?.name && (
              <DetailsItem
                title="Product Name"
                subtitle={selectedProduct?.name}
              />
            )}
            {selectedProduct?.supplier_name && (
              <DetailsItem
                title="Supplier Name"
                subtitle={selectedProduct?.supplier_name}
              />
            )}
            {selectedProduct?.description && (
              <EntityFileDetails.DetailExpandableText
                title="Product Description"
                text={selectedProduct?.description}
              />
            )}
            <Divider />
            <FormSelectField
              name="quote"
              label="Select Quote"
              control={methods.control}
              options={quotes}
              valueName="id"
              optionName="name"
            />
            <Typography
              component="p"
              variant="caption"
              sx={{ fontWeight: 400 }}
            >
              There are multiple quotes. Please select which quote you want to add this to.
            </Typography>
            {selectedProduct?.variants && selectedProduct?.variants.length > 0 && (
              <VariantField
                id="variant"
                control={methods.control}
                name="variant"
                hasError={!!methods.formState.errors.variant}
                error={methods.formState.errors.variant?.message}
                label="Options"
                options={variants}
                valueName="id"
                optionName="name"
                optionPrice="price"
              />
            )}
          </FormProvider>
        </DrawerContent.Content>
        <DrawerContent.ActionButtons
          submitButtonText="Add"
          submitButtonLoading={loading}
          submit={handleSave}
          close={close}
        />
      </DrawerContent.Wrapper>
    </DrawerContent>
  );
};
